import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Paper, Typography, useTheme } from "@mui/material/";
import LockIcon from "@mui/icons-material/Lock";
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import useLogout from "../../hooks/useLogout";
import { getColumnsModel, getConfigOptions, mapObjectToLabels } from "../../pages/Statistics/utils";
import {
  buildCrashListDatasetQueryParams,
  CRASH_LIST_DATASET_FILTER_MERGED,
  getDefaultCrashListDatasetFilter,
  shouldLockCrashListDatasetToMerged,
} from "../../pages/Statistics/statistics-dataset-context";
import { StatsDatasetControls } from "./StatsDatasetControls";
import { GridToolbarContainer, GridToolbarExport, useGridApiRef, getGridStringOperators, getGridDateOperators } from "@mui/x-data-grid";
import useHttp from "../../hooks/useHttp";
import StatisticsFilterPanel from "./StatisticsFilterPanel";
import useHttpApiNew from "../../hooks/useHttpApiNew";
import { useConfigLocale } from "../../hooks/use-config-locale";
import { StripedDataGrid } from "../tables/StripedDataGrid";
import { NewApiBaseUrl } from "../../consts/new-api-base-url";
import { NAV_LAYOUT_MARGIN_START } from "../../consts/nav-layout-margin";
import {
  appendCrashListSortParams,
  countDistinctCrashesInPage,
  CRASH_LIST_DEFAULT_PAGE_SIZE,
  CRASH_LIST_PAGE_SIZE_OPTIONS,
  estimateCrashListRowCount,
  getCrashListLimitOffset,
  getCrashListSortableFieldSet,
  mapCrashListFilterModelToQueryParams,
  mergeCrashListFilterParams,
  parseCrashCountResponse,
  parseCrashListTotalCount,
} from "../../utils/crash-list-pagination";

const newGetAllCrashesUrl = new URL(`${NewApiBaseUrl}/crash/all`);
const newCrashCountUrl = new URL(`${NewApiBaseUrl}/crash/count`);

const DEFAULT_PAGINATION_MODEL = { page: 0, pageSize: CRASH_LIST_DEFAULT_PAGE_SIZE };

const CRASH_LIST_STRING_FILTER_OPS = new Set(['contains', 'equals', 'startsWith', 'endsWith']);
const CRASH_LIST_DATE_FILTER_OPS = new Set(['is', 'after', 'onOrAfter', 'before', 'onOrBefore']);

const crashListStringFilterOperators = getGridStringOperators().filter((op) =>
  CRASH_LIST_STRING_FILTER_OPS.has(op.value),
);
const crashListDateFilterOperators = getGridDateOperators().filter((op) =>
  CRASH_LIST_DATE_FILTER_OPS.has(op.value),
);

function buildPreparedRows(crashes, configuration, organizations) {
  if (!Array.isArray(crashes) || crashes.length === 0) return []
  const configOptions = getConfigOptions(configuration, organizations)
  const mappedCrashes = crashes.map((crash) => mapObjectToLabels(crash, configOptions))
  return mappedCrashes.map((crash, i) => {
    const source = crashes[i]
    const gridId = source?.id ?? i + 1
    return {
      id: gridId,
      ...(crash.road ?? {}),
      ...(crash.unit ?? {}),
      ...(crash.unit?.people ?? {}),
      ...(crash.people ?? {}),
      ...(crash.crash ?? {}),
      ...crash,
      id: gridId,
      organizationValue: source?.crash?.organization ?? source?.organization,
      is_closed: source?.crash?.['is_closed'] ?? source?.['is_closed'],
    }
  })
}

const crashListToolbarContainerSx = (theme) => ({
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 1.5,
  px: 1.5,
  py: 0.75,
  minHeight: 44,
  alignItems: 'center',
  '& .MuiButton-root': {
    color: '#ffffff',
    fontWeight: 700,
    fontSize: 13,
    minHeight: 30,
    px: 1.25,
    textTransform: 'none',
    border: '1px solid rgba(255, 255, 255, 0.55)',
    borderRadius: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.22)',
      borderColor: '#ffffff',
    },
    '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
      color: theme.palette.common.white,
    },
  },
  '& .MuiFormControlLabel-label': {
    color: theme.palette.common.white,
    fontSize: 13,
    fontWeight: 500,
  },
});

const CrashList = ({ isMobile, handleRowClick }) => {
  const { _ } = useLingui();
  const configLocale = useConfigLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const state = location.state;
  const token = state?.token || null;
  const init = useMemo(() => state?.init || {}, [state?.init]);
  const theme = useTheme();
  const { error } = useHttp();
  const [crashesLoading, setCrashesLoading] = useState(true);
  const [crashes, setCrashes] = useState([]);
  const [user, setUser] = useState({});
  const [rows, setRows] = useState([]);
  const [formModel, setFormModel] = useState({});
  const [formList, setFormList] = useState([]);
  const [message, setMessage] = useState('');
  const [columns, setColumns] = useState([]);
  const [columnGroupingModel, setColumnGroupingModel] = useState([]);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [selectedCrashRow, setSelectedCrashRow] = useState(null);
  const [paginationModel, setPaginationModel] = useState(DEFAULT_PAGINATION_MODEL);
  const [sortModel, setSortModel] = useState(/** @type {import('@mui/x-data-grid').GridSortModel} */([]));
  const [filterModel, setFilterModel] = useState(
    /** @type {import('@mui/x-data-grid').GridFilterModel} */({ items: [] }),
  );
  const [totalCrashCount, setTotalCrashCount] = useState(0);
  const [activeFilters, setActiveFilters] = useState(/** @type {Record<string, string>|null} */null);
  const [highlightedCrashId, setHighlightedCrashId] = useState(/** @type {string|null} */null);
  const [datasetFilter, setDatasetFilter] = useState(() =>
    getDefaultCrashListDatasetFilter(state?.init?.user),
  );
  const lockDatasetToMerged = useMemo(
    () => shouldLockCrashListDatasetToMerged(init?.user),
    [init?.user],
  );
  const apiRef = useGridApiRef();
  const { request } = useHttpApiNew();
  const highlightCrashId = state?.highlightCrashId != null ? String(state.highlightCrashId) : null;

  const handleLockClick = useCallback((row) => {
    setSelectedCrashRow(row);
    setLockDialogOpen(true);
  }, []);
  const handleLockDialogClose = useCallback(() => {
    setLockDialogOpen(false);
    setSelectedCrashRow(null);
  }, []);
  const handleConfirmModify = useCallback(() => {
    if (selectedCrashRow) {
      handleRowClick({ row: selectedCrashRow, fromLockConfirm: true });
      navigate('/home', { state: { token, init } });
    }
    handleLockDialogClose();
  }, [selectedCrashRow, handleLockDialogClose, handleRowClick, navigate, token, init]);

  const appendCrashListQueryParams = useCallback((url, { datasetFilter: useDatasetFilter, filters }) => {
    const datasetParams = buildCrashListDatasetQueryParams({
      datasetFilter: useDatasetFilter,
      user: init?.user,
    });
    Object.entries(datasetParams).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }
  }, [init?.user]);

  const resolveTotalCrashCount = useCallback(async ({
    responseData,
    page,
    pageSize,
    pageList,
    datasetFilter: useDatasetFilter,
    filters,
  }) => {
    const fromAll = parseCrashListTotalCount(responseData);
    if (fromAll != null) return fromAll;

    try {
      const countUrl = new URL(newCrashCountUrl);
      appendCrashListQueryParams(countUrl, {
        datasetFilter: useDatasetFilter,
        filters,
      });
      const countRes = await request(countUrl, 'GET', null, { Authorization: `Bearer ${token}` });
      if (!countRes?.responseAnyError) {
        const fromCount = parseCrashCountResponse(countRes.responseData);
        if (fromCount != null) return fromCount;
      }
    } catch {
      /* fallback below */
    }

    return estimateCrashListRowCount({
      page,
      pageSize,
      pageDistinctCount: countDistinctCrashesInPage(pageList),
    });
  }, [appendCrashListQueryParams, request, token]);

  /**
   * Server-paginated fetch. Backend supports distinct-crash LIMIT/OFFSET,
   * totalCount, sort/order, and field filters (equal/from/to + contains* when available).
   * Falls back to /crash/count or estimate if totalCount is missing.
   */
  const getCrashes = useCallback(async (data, options = {}) => {
    const nextPage = options.page ?? paginationModel.page;
    const nextPageSize = options.pageSize ?? paginationModel.pageSize;
    const nextSortModel = options.sortModel ?? sortModel;
    const nextFilterModel = options.filterModel ?? filterModel;
    const useDatasetFilter = options.datasetFilter ?? datasetFilter;
    const panelFilters = data !== undefined ? data : activeFilters;
    const gridFilters = mapCrashListFilterModelToQueryParams(
      nextFilterModel,
      init?.configuration,
    );
    const filters = mergeCrashListFilterParams(panelFilters, gridFilters);
    const resetPage = options.resetPage === true;

    setCrashesLoading(true);
    try {
      const { limit, offset } = getCrashListLimitOffset(
        resetPage ? 0 : nextPage,
        nextPageSize,
      );
      const allCrashesUrl = new URL(newGetAllCrashesUrl);
      allCrashesUrl.searchParams.set('format', 'table');
      allCrashesUrl.searchParams.set('limit', String(limit));
      allCrashesUrl.searchParams.set('offset', String(offset));
      appendCrashListQueryParams(allCrashesUrl, {
        datasetFilter: useDatasetFilter,
        filters,
      });
      appendCrashListSortParams(allCrashesUrl, nextSortModel, init?.configuration);

      const res = await request(allCrashesUrl, 'GET', null, { Authorization: `Bearer ${token}` });
      if (res.responseAnyError) return;

      const list = res.responseData?.crashes ?? [];
      // Preserve server order (do not re-sort client-side).
      const pageCrashes = list.map((crash, i) => ({ ...crash, id: i + 1 }));

      setCrashes(pageCrashes);
      if (data !== undefined) setActiveFilters(data);
      if (resetPage) {
        setPaginationModel((prev) => ({ ...prev, page: 0, pageSize: nextPageSize }));
      }

      const total = await resolveTotalCrashCount({
        responseData: res.responseData,
        page: resetPage ? 0 : nextPage,
        pageSize: nextPageSize,
        pageList: pageCrashes,
        datasetFilter: useDatasetFilter,
        filters,
      });
      setTotalCrashCount(total);
    } finally {
      setCrashesLoading(false);
    }
  }, [
    token,
    request,
    datasetFilter,
    activeFilters,
    sortModel,
    filterModel,
    init?.configuration,
    paginationModel.page,
    paginationModel.pageSize,
    appendCrashListQueryParams,
    resolveTotalCrashCount,
  ]);

  const handlePaginationModelChange = useCallback((model) => {
    const pageChanged = model.page !== paginationModel.page;
    const sizeChanged = model.pageSize !== paginationModel.pageSize;
    setPaginationModel(sizeChanged ? { ...model, page: 0 } : model);
    if (!pageChanged && !sizeChanged) return;
    getCrashes(activeFilters, {
      page: sizeChanged ? 0 : model.page,
      pageSize: model.pageSize,
      resetPage: sizeChanged,
    });
  }, [paginationModel.page, paginationModel.pageSize, getCrashes, activeFilters]);

  const handleSortModelChange = useCallback((model) => {
    setSortModel(model);
    getCrashes(activeFilters, {
      page: 0,
      pageSize: paginationModel.pageSize,
      resetPage: true,
      sortModel: model,
    });
  }, [getCrashes, activeFilters, paginationModel.pageSize]);

  const handleFilterModelChange = useCallback((model) => {
    setFilterModel(model);
    getCrashes(activeFilters, {
      page: 0,
      pageSize: paginationModel.pageSize,
      resetPage: true,
      filterModel: model,
    });
  }, [getCrashes, activeFilters, paginationModel.pageSize]);

  useEffect(() => {
    if (!token) return logout();
    if (isMobile) return navigate('/home', { state: { token, init } });
    getCrashes(null, { page: 0, pageSize: paginationModel.pageSize, resetPage: true });
    // Initial load only when auth/init ready — pagination/filter refetch via handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, logout, isMobile, navigate, init]);

  useEffect(() => {
    if (!highlightCrashId || crashesLoading || !rows.length) return;

    const rowIndex = rows.findIndex(
      (row) => String(row['Crash id']) === highlightCrashId
    );
    if (rowIndex < 0) {
      // Server pagination: crash may be on another page — highlight only if present here.
      navigate('.', {
        replace: true,
        state: { ...(state || {}), highlightCrashId: undefined }
      });
      return;
    }

    setHighlightedCrashId(highlightCrashId);

    navigate('.', {
      replace: true,
      state: { ...(state || {}), highlightCrashId: undefined }
    });
  }, [highlightCrashId, crashesLoading, rows, navigate, state]);

  useEffect(() => {
    if (!highlightedCrashId || !rows.length) return;

    const rowIndex = rows.findIndex(
      (row) => String(row['Crash id']) === highlightedCrashId
    );
    if (rowIndex < 0) return;

    const scrollTimer = window.setTimeout(() => {
      apiRef.current?.scrollToIndexes({ rowIndex });
    }, 200);

    return () => window.clearTimeout(scrollTimer);
  }, [highlightedCrashId, rows, paginationModel.page, apiRef]);

  useEffect(() => {
    const { configuration, organizations, user } = init;
    setUser(user);
    const preparedRows = buildPreparedRows(crashes, configuration, organizations);
    setRows(preparedRows);
    setFormModel(configuration);
    const initForms = Object.keys(configuration).sort((a, b) => configuration[a].position - configuration[b].position);
    setFormList(initForms);
    const translateColumn = (label) => {
      if (label === 'Report Type') return _(msg`Report Type`);
      if (label === 'Merged') return _(msg`Merged`);
      return label;
    };
    const { columns: modelColumns, columnGroupingModelWOrg: columnGroupingModel } = getColumnsModel(configuration, translateColumn);
    const sortableFields = getCrashListSortableFieldSet(configuration);
    const columnsWithServerFlags = modelColumns.map((col) => {
      const isDateCol = /\bdate\b/i.test(col.field);
      const base = {
        ...col,
        sortable: sortableFields.has(col.field),
      };
      if (col.field === 'organization') {
        return { ...base, filterable: false };
      }
      return {
        ...base,
        filterOperators: isDateCol ? crashListDateFilterOperators : crashListStringFilterOperators,
      };
    });
    const isAdmin = init?.user?.type === 1;
    const userOrg = init?.user?.organization;
    const managedOrgIds = Array.isArray(init?.user?.organization_ids) ? init.user.organization_ids : [];
    const hasHealthcareOrg = (init?.organizations ?? []).some((o) =>
    managedOrgIds.includes(Number(o.id ?? o.value)) && Number(o.basic_id ?? o.basic) === 2
    );
    const showFirstColumn = isAdmin || userOrg === 2 || hasHealthcareOrg;
    const lockColumn = showFirstColumn ? {
      field: 'validated_lock',
      headerName: '',
      width: 36,
      minWidth: 36,
      sortable: false,
      filterable: false,
      headerClassName: 'overload-theme--header',
      rowSpanValueGetter: (value, row) => (row ? row['Crash id'] : null),
      renderCell: (params) => {
        if (params.row.merge_date != null && params.row.merge_date !== '') {
          if (isAdmin) {
            return (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLockClick(params.row);
                  }}>
                  
                                    <LockIcon fontSize="small" />
                                </IconButton>
                            </Box>);

          }
          return <Box sx={{ width: '100%', height: '100%' }} />;
        }
        if (params.row.organizationValue === 2 && params.row.is_closed !== undefined) {
          const isClosed = params.row.is_closed === true;
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                            <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  backgroundColor: isClosed ? 'success.main' : 'error.main'
                }}
                title={isClosed ? _(msg`Closed`) : _(msg`Open`)} />
              
                        </Box>);

        }
        return null;
      }
    } : null;
    setColumns(lockColumn ? [lockColumn, ...columnsWithServerFlags] : columnsWithServerFlags);
    setColumnGroupingModel(columnGroupingModel);
  }, [init, crashes, handleLockClick, configLocale, _]);

  useEffect(() => {
    if (totalCrashCount <= 0) return;
    const maxPage = Math.max(0, Math.ceil(totalCrashCount / paginationModel.pageSize) - 1);
    if (paginationModel.page <= maxPage) return;
    setPaginationModel((prev) => ({ ...prev, page: maxPage }));
    getCrashes(activeFilters, { page: maxPage, pageSize: paginationModel.pageSize });
  }, [
    totalCrashCount,
    paginationModel.page,
    paginationModel.pageSize,
    activeFilters,
    getCrashes,
  ]);



  const groupedRows = useMemo(() => {
    if (!rows?.length) {
      return [];
    }
    const groupBy = Object.groupBy(rows, (item) => item?.['Crash id']);
    const toArray = Object.entries(groupBy || {})?.map(([k, v], i) => [k, i % 2 === 0]);
    const reduced = toArray?.reduce((acc, [k, v]) => {
      return { ...acc, ...{ [k]: v } };
    }, {});
    return reduced;
  }, [rows]);

  const handleFilterSubmit = (searchParams) => {
    getCrashes(searchParams, {
      page: 0,
      pageSize: paginationModel.pageSize,
      resetPage: true,
    });
  };

  const handleFilterReset = () => {
    setFilterModel({ items: [] });
    getCrashes(null, {
      page: 0,
      pageSize: paginationModel.pageSize,
      resetPage: true,
      filterModel: { items: [] },
    });
  };

  const TopToolbar = ({ totalCrashes }) => {
    return (
      <GridToolbarContainer sx={crashListToolbarContainerSx(theme)}>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <GridToolbarExport />
                  {/* Export covers the current page only (server pagination). */}
                  <StatsDatasetControls
                    user={init.user}
                    organizations={init.organizations ?? []}
                    reportType={0}
                    basicOrganization={
                      lockDatasetToMerged ? CRASH_LIST_DATASET_FILTER_MERGED : datasetFilter
                    }
                    onBasicOrganizationChange={(value) => {
                      if (lockDatasetToMerged) return
                      setDatasetFilter(value);
                      getCrashes(activeFilters, {
                        datasetFilter: value,
                        page: 0,
                        pageSize: paginationModel.pageSize,
                        resetPage: true,
                      });
                    }}
                    showReportType={false}
                    includeMergedOption
                    lockBasicOrganizationToMerged={lockDatasetToMerged}
                    variant="toolbar"
                    isDark={theme.palette.mode === 'dark'}
                    theme={theme}
                  />
                </Box>
                <Typography
                  align="right"
                  sx={{
                    color: theme.palette.common.white,
                    fontSize: 13,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}>
                    <Trans>Crashes found</Trans>: {totalCrashes}
                </Typography>
            </GridToolbarContainer>);

  };


  return (
    <>
            <Box
        pl={2}
        pr={2}
        display='flex'
        flexDirection='column'
        alignItems='stretch'
        sx={{
          marginInlineStart: NAV_LAYOUT_MARGIN_START,
          '& .overload-theme--header': {
            backgroundColor: theme.mode === 'light' && theme.palette.primary.main,
            color: theme.mode === 'light' && theme.palette.primary.contrastText
          }
        }}>
        
                {formList.length > 0 &&
        <StatisticsFilterPanel
          newLabel={_(msg`Crash list`)}
          title={_(msg`Crashes`)}
          formList={formList}
          filterDisabled={crashesLoading}
          onFilterSubmit={handleFilterSubmit}
          onFilterReset={handleFilterReset}
          user={user}
          formModel={formModel}
          isMobile={isMobile} />

        }
                {formList.length > 0 &&
        <Paper
          sx={{ mb: 2, display: 'flex', flexDirection: 'column', minHeight: '50vh', position: 'relative' }}>
                        <StripedDataGrid
            apiRef={apiRef}
            loading={crashesLoading}
            localeText={{
              toolbarExport: _(msg`Export`),
              toolbarExportCSV: _(msg`Export`)
            }}
            showColumnVerticalBorder
            showCellVerticalBorder
            unstable_rowSpanning={!crashesLoading}
            getRowId={(row) => String(row.id)}
            rows={rows}
            onRowClick={(params) => {
              handleRowClick(params);
              navigate('/home', { state: { token, init } });
            }}
            columns={columns}
            columnGroupingModel={columnGroupingModel}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            paginationMode="server"
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            filterDebounceMs={400}
            rowCount={totalCrashCount}
            initialState={{
              columns: { columnVisibilityModel: { id: false } }
            }}
            pageSizeOptions={CRASH_LIST_PAGE_SIZE_OPTIONS}
            getRowClassName={(params) => {
              const stripe = groupedRows?.[params?.row?.['Crash id']] ? 'even' : 'odd';
              const highlighted =
              highlightedCrashId &&
              String(params?.row?.['Crash id']) === highlightedCrashId ?
              'highlighted-crash-row' :
              '';
              return [stripe, highlighted].filter(Boolean).join(' ');
            }}
            sx={{
              '& .MuiDataGrid-row.highlighted-crash-row': {
                boxShadow: `inset 4px 0 0 ${theme.palette.newColors?.yellow || '#E0D028'}`,
                backgroundColor: 'rgba(246, 213, 82, 0.35)'
              }
            }}
            slots={{
              toolbar: TopToolbar
            }}
            slotProps={{
              toolbar: {
                totalCrashes: totalCrashCount
              }
            }} />
          
                    </Paper>
        }
            </Box>
            {error && <Alert sx={{ mt: 2 }} severity="error">
                <Trans>There was an error: {error}.</Trans>
            </Alert>}
            {message && <Alert sx={{ mt: 2 }} severity='success'>
                {message}
            </Alert>}
            <Dialog open={lockDialogOpen} onClose={handleLockDialogClose}>
                <DialogTitle sx={{ color: theme.palette.newColors.gray }}>
                    <Trans>Modify validated crash</Trans>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Trans>This crash is already recorded in the national database. Do you want to proceed with the modification?</Trans>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="error" onClick={handleLockDialogClose}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button
            variant="outlined"
            sx={{ color: theme.palette.newColors.gray, fontWeight: 600 }}
            onClick={handleConfirmModify}
            autoFocus>
            
                        <Trans>Confirm</Trans>
                    </Button>
                </DialogActions>
            </Dialog>
        </>);

};

export default CrashList;